// Imported before `App` so that Sentry is initialised before the router and the store are created.
import * as Sentry from '@sentry/react';
import { reactRouterBrowserTracingIntegration } from '@sentry/react/react-router';

import packageJson from '../package.json';
import { getUrlParam } from './utils/urlUtils';
import { beforeSend, buildSentryUser } from './utils/sentryUtils';
import { reportUnsupportedBrowser } from 'utils/browserSupport';

const { name: appName, version: appVersion } = packageJson;

if (process.env.IS_SENTRY_ENABLED === 'true') {
  const publicToken = getUrlParam('token');
  Sentry.setTag('SO_public_token', publicToken);
  // Streamed spans (v11 default) only carry attributes, not tags: also set the token as an attribute to keep spans searchable.
  if (publicToken) {
    Sentry.setAttribute('SO_public_token', publicToken);
  }
  Sentry.setUser(buildSentryUser({ publicToken }));

  Sentry.init({
    dsn: 'https://ba8db2ccbf37470582355c3550c2c496@o326541.ingest.sentry.io/4504045178388480',
    release: `${appName}@${appVersion}`,
    normalizeDepth: 3,
    tracesSampleRate: 0.1,
    environment: 'production',
    // Sentry v11 collects user info (IP), cookies, headers and HTTP bodies by default: keep the v10 (no PII) baseline.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      },
      httpBodies: [],
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
    },
    integrations: [
      reactRouterBrowserTracingIntegration(),
      // Sentry v11 defaults to one session per page load: keep one per navigation (v10) for comparable release health.
      Sentry.browserSessionIntegration({ lifecycle: 'route' }),
    ],
    beforeSend,
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      'Can\'t find variable: ZiteReader',
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Firefox for iOS reader-mode bridge injected into the page (not our code)
      /__firefox__/,
      // Safari/iOS injected script noise (not our code)
      'Can\'t find variable: EmptyRanges',
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  });

  reportUnsupportedBrowser();
}
